import type { CrossCheckClause, PastAgreementOption } from '../types/crossCheck';

export const SIMILARITY_FILTER_MIN = 95;
export const SIMILARITY_FILTER_MAX = 100;
export const SIMILARITY_FILTER_PRESETS = [100, 99, 98, 97, 96, 95] as const;

export function formatSimilarityFilterLabel(threshold: number): string {
  return threshold === 100 ? '100%' : `≥${threshold}%`;
}

export function isSimilarityFilterThreshold(value: number): boolean {
  return (
    Number.isInteger(value) &&
    value >= SIMILARITY_FILTER_MIN &&
    value <= SIMILARITY_FILTER_MAX
  );
}

export function clauseMaxSimilarity(clause: CrossCheckClause): number | null {
  const options = clause.past_agreement?.options;
  if (!options?.length) return null;
  let max = -1;
  for (const option of options) {
    if (typeof option.similarity === 'number' && !Number.isNaN(option.similarity)) {
      max = Math.max(max, option.similarity);
    }
  }
  return max >= 0 ? max : null;
}

export function clauseHasFinalVersionMatch(
  clause: CrossCheckClause,
  finalVersionById: Record<string, boolean>,
): boolean {
  const options = clause.past_agreement?.options;
  if (!options?.length) return false;
  return options.some(
    (option) =>
      option.is_final_version === true ||
      (!!option.id && finalVersionById[option.id] === true),
  );
}

export function clauseMatchesSimilarityFilter(
  clause: CrossCheckClause,
  threshold: number,
): boolean {
  const similarity = clauseMaxSimilarity(clause);
  if (similarity == null) return false;
  if (threshold === 100) return similarity === 100;
  return similarity >= threshold;
}

export function isClauseClosable(clause: CrossCheckClause): boolean {
  const isLocked = clause.lock_status?.locked ?? false;
  const isSubmittedToResponse = clause.submission_status?.response_submitted ?? false;
  const isSubmittedToSchedule = clause.submission_status?.schedule_submitted ?? false;
  return !isLocked && !isSubmittedToResponse && !isSubmittedToSchedule;
}

export function parsePastNegotiationAuthors(authorValue: string | undefined | null): string[] {
  if (!authorValue?.trim()) return [];
  return authorValue
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
}

export function collectPastNegotiationAuthors(
  clauses: CrossCheckClause[],
  authorById: Record<string, string>,
): string[] {
  const set = new Set<string>();
  for (const clause of clauses) {
    const options = clause.past_agreement?.options;
    if (!options?.length) continue;
    for (const option of options) {
      const id = option?.id?.trim();
      if (!id) continue;
      for (const author of parsePastNegotiationAuthors(authorById[id])) {
        set.add(author);
      }
    }
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

export function filterPastAgreementOptionsByAuthors(
  options: PastAgreementOption[],
  selectedAuthors: Set<string> | null,
  authorById: Record<string, string>,
): PastAgreementOption[] {
  if (selectedAuthors === null) return options;
  return options.filter((option) => {
    const id = option?.id?.trim();
    if (!id) return false;
    const authors = parsePastNegotiationAuthors(authorById[id]);
    if (!authors.length) return false;
    return authors.some((author) => selectedAuthors.has(author));
  });
}

export function formatAuthorFilterLabel(selectedAuthors: Set<string> | null): string | null {
  if (selectedAuthors === null) return null;
  if (selectedAuthors.size === 0) return 'None';
  const names = Array.from(selectedAuthors).sort((a, b) => a.localeCompare(b));
  if (names.length === 1) return names[0];
  return `${names.length} authors`;
}

export type PastNegotiationTimeFilter = Set<number> | null;

/** Parse playbook `timestamp` (YYYY-MM-DD HH:MM:SS) or ISO raw form to epoch ms. */
export function parsePlaybookTimestampMs(timestamp: string | undefined | null): number | null {
  if (!timestamp?.trim()) return null;
  const trimmed = timestamp.trim();
  const normalized = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const withZone = normalized.endsWith('Z') ? normalized : `${normalized}Z`;
  const ms = Date.parse(withZone);
  return Number.isNaN(ms) ? null : ms;
}

export function collectPastNegotiationYears(
  clauses: CrossCheckClause[],
  timestampById: Record<string, string>,
): number[] {
  const years = new Set<number>();
  for (const clause of clauses) {
    const options = clause.past_agreement?.options;
    if (!options?.length) continue;
    for (const option of options) {
      const id = option?.id?.trim();
      if (!id) continue;
      const ms = parsePlaybookTimestampMs(timestampById[id]);
      if (ms != null) years.add(new Date(ms).getUTCFullYear());
    }
  }
  return Array.from(years).sort((a, b) => b - a);
}

export function pastNegotiationTimestampInFilter(
  timestamp: string | undefined | null,
  selectedYears: PastNegotiationTimeFilter,
): boolean {
  if (selectedYears === null) return true;
  const ms = parsePlaybookTimestampMs(timestamp);
  if (ms == null) return false;
  return selectedYears.has(new Date(ms).getUTCFullYear());
}

export function filterPastAgreementOptionsByTime(
  options: PastAgreementOption[],
  timeFilter: PastNegotiationTimeFilter,
  timestampById: Record<string, string>,
): PastAgreementOption[] {
  if (timeFilter === null) return options;
  return options.filter((option) => {
    const id = option?.id?.trim();
    if (!id) return false;
    return pastNegotiationTimestampInFilter(timestampById[id], timeFilter);
  });
}

export function filterPastAgreementOptions(
  options: PastAgreementOption[],
  opts: {
    authorFilter: Set<string> | null;
    timeFilter: PastNegotiationTimeFilter;
    authorById: Record<string, string>;
    timestampById: Record<string, string>;
  },
): PastAgreementOption[] {
  const byAuthor = filterPastAgreementOptionsByAuthors(
    options,
    opts.authorFilter,
    opts.authorById,
  );
  return filterPastAgreementOptionsByTime(byAuthor, opts.timeFilter, opts.timestampById);
}

export function formatTimeFilterLabel(selectedYears: PastNegotiationTimeFilter): string | null {
  if (selectedYears === null) return null;
  if (selectedYears.size === 0) return 'None';
  const years = Array.from(selectedYears).sort((a, b) => b - a);
  if (years.length === 1) return String(years[0]);
  return `${years.length} years`;
}

export function applyClauseEditorFilters(
  clauses: CrossCheckClause[],
  opts: {
    finalVersion?: boolean;
    minSimilarity?: number | null;
    finalVersionById?: Record<string, boolean>;
  },
): CrossCheckClause[] {
  let list = clauses;
  if (opts.finalVersion) {
    const byId = opts.finalVersionById ?? {};
    list = list.filter((clause) => clauseHasFinalVersionMatch(clause, byId));
  }
  if (opts.minSimilarity != null) {
    list = list.filter((clause) => clauseMatchesSimilarityFilter(clause, opts.minSimilarity!));
  }
  return list;
}
